<script setup lang="ts">
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { useTransactionSummaryCalclation } from '@/InventoryControlModule/composables/useTransactionSummaryCalclation';
import { InventoryTableViewModel } from '@/InventoryControlModule/pages/inventoryTableViewModel';
import { IInventoryOrder } from '@/InventoryControlModule/domains/entities/InventoryOrder';
import { IBeforeAfterValue } from '@/InventoryControlModule/domains/entities/MonthlyData';
import { computed } from 'vue';
import { SUMMARY_ROW_BLANK_HEADER_KEYS, BEFORE_AFTER_KEYS } from '../../constants/';

/**
 * TransactionSummaryColumn コンポーネントのプロパティ
 */
interface Props {
  /** 対象の月 (相対月) */
  month: number;
  /** 月次データ */
  data: any;
  /** 表示対象のヘッダーリスト */
  headerList: string[];
  /** インベントリViewModel */
  inventoryVm: InventoryTableViewModel;
  /** 月次エンドヘッダー */
  monthlyEndHeader: string;
  /** 開いているページ情報 */
  isOrderMode?: boolean;
  /** 発注編集データ（発注モード時のみ） */
  orderEditData?: IInventoryOrder;
}

const props = withDefaults(defineProps<Props>(), {
  isOrderMode: false,
  // eslint-disable-next-line jsdoc/require-jsdoc
  orderEditData: () => ({}),
});

const { getTransactionData, getExpectedEndInventoryBeforeAfter, getTurnOverBeforeAfter } = useTransactionSummaryCalclation();

/**
 * 発注前後の値を取得する
 * @param key - トランザクションキー
 */
const getBeforeAfterValue = (key: string): IBeforeAfterValue | null => {
  if (!props.isOrderMode) return null;
  if (key === 'expectedEndInventory') {
    return getExpectedEndInventoryBeforeAfter(props.data, props.month, props.orderEditData);
  }
  if (key === 'turnOver') {
    return getTurnOverBeforeAfter(props.data, props.month, props.orderEditData);
  }
  return null;
};

const transactionSummaryDataList = computed(() => {
  return props.inventoryVm
    .getMonthlyTransactionHeaderKeys(props.month, props.isOrderMode)
    .map((key) => {
      if (!props.headerList.includes(`transaction-${props.month}-${key}`)) return null;
      const amount = getTransactionData(props.data, key, props.month, props.isOrderMode, props.orderEditData);
      const beforeAfter = getBeforeAfterValue(key);
      return {
        key,
        amount,
        isNegative: amount < 0,
        beforeAfter,
        showBeforeAfter: props.isOrderMode && beforeAfter?.hasChange && (BEFORE_AFTER_KEYS as readonly string[]).includes(key),
      };
    })
    .filter((data) => data !== null);
});
</script>

<template>
  <!--<月次集計データ列 -->
  <template v-for="transactionSummaryData in transactionSummaryDataList" :key="transactionSummaryData.key">
    <!-- 何も表示しない -->
    <EzCell
      v-if="(SUMMARY_ROW_BLANK_HEADER_KEYS as readonly string[]).includes(transactionSummaryData.key)"
      is-number
      :has-right-border="transactionSummaryData.key === monthlyEndHeader"
      class="table-cell table-cell--number table-cell--bold table-cell--totalization"
    />

    <!-- 発注前後の値を表示（予末在庫・回転率） -->
    <EzCell
      v-else-if="transactionSummaryData.showBeforeAfter && transactionSummaryData.beforeAfter"
      is-number
      :has-right-border="transactionSummaryData.key === monthlyEndHeader"
      class="table-cell table-cell--number table-cell--bold table-cell--totalization"
    >
      <span class="table-cell--bold table-cell--before-after">
        <span :class="{ 'table-cell--negative': transactionSummaryData.beforeAfter.before < 0 }">
          {{ transactionSummaryData.beforeAfter.before.roundToDecimal(2).addComma() }}
        </span>
        <span class="table-cell--arrow">→</span>
        <span :class="{ 'table-cell--negative': transactionSummaryData.beforeAfter.after < 0 }">
          {{ transactionSummaryData.beforeAfter.after.roundToDecimal(2).addComma() }}
        </span>
      </span>
    </EzCell>

    <!-- それ以外 -->
    <EzCell
      v-else
      is-number
      :is-negative="transactionSummaryData.isNegative"
      :has-right-border="transactionSummaryData.key === monthlyEndHeader"
      class="table-cell table-cell--number table-cell--bold table-cell--totalization"
    >
      <span class="table-cell--bold"> {{ transactionSummaryData.amount.roundToDecimal(2).addComma() }} </span>
    </EzCell>
  </template>
</template>

<style lang="scss" scoped>
@import '@/InventoryControlModule/pages/scss/inventory-table.scss';
</style>
